import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { useEffect, useRef } from "react";

import { AdminView } from "@/components/AdminView";
import { AutomationDetail } from "@/components/automations/AutomationDetail";
import { AutomationList } from "@/components/automations/AutomationList";
import { CliAuthView } from "@/components/CliAuthView";
import { ConversationView } from "@/components/ConversationView";
import { DocsView } from "@/components/DocsView";
import { LoginPage } from "@/components/LoginPage";
import { MobileHeader } from "@/components/MobileHeader";
import { PreferencesView } from "@/components/PreferencesView";
import { Sidebar } from "@/components/Sidebar";
import { Toast } from "@/components/Toast";
import { UsageStatsView } from "@/components/UsageStatsView";
import { WatchesView } from "@/components/watches/WatchesView";
import { WelcomeView } from "@/components/WelcomeView";
import { useAnswerReadyNotifications } from "@/hooks/useAnswerReadyNotifications";
import { useModels } from "@/hooks/useModels";
import { usePreferences } from "@/hooks/usePreferences";
import { useSession } from "@/lib/auth";
import { useStore } from "@/lib/store";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000, // Consider data stale after 1 second
      retry: 1,
    },
  },
});

function MainContent(): JSX.Element {
  const activeView = useStore((state) => state.activeView);
  const activeSessionKey = useStore((state) => state.activeSessionKey);

  if (activeView === "admin") return <AdminView />;
  if (activeView === "automations") return <AutomationList />;
  if (activeView === "automation-detail") return <AutomationDetail />;
  if (activeView === "watches") return <WatchesView />;
  if (activeView === "cli-auth") return <CliAuthView />;
  if (activeView === "preferences") return <PreferencesView />;
  if (activeView === "docs") return <DocsView />;
  if (activeView === "stats") return <UsageStatsView />;
  if (activeSessionKey) return <ConversationView />;
  return <WelcomeView />;
}

function Dashboard() {
  const selectSession = useStore((state) => state.selectSession);
  const selectAutomation = useStore((state) => state.selectAutomation);
  const setDocSlug = useStore((state) => state.setDocSlug);
  const setView = useStore((state) => state.setView);
  const setUser = useStore((state) => state.setUser);
  const setComposerMode = useStore((state) => state.setComposerMode);
  const setComposerModelId = useStore((state) => state.setComposerModelId);
  const { data: sessionData } = useSession();
  useAnswerReadyNotifications({ enabled: !!sessionData?.user });
  const { data: preferencesData, isPending: isPreferencesPending } = usePreferences();
  const { data: modelsData, isPending: isModelsPending } = useModels();
  const defaultsHydratedRef = useRef(false);

  // Sync user to store when session loads, fetching role from /v1/me
  useEffect(() => {
    if (sessionData?.user) {
      // Set initial user data from session immediately
      setUser({
        id: sessionData.user.id,
        name: sessionData.user.name,
        email: sessionData.user.email,
        image: sessionData.user.image ?? null,
        role: "member", // default until /v1/me responds
      });

      // Fetch role from the API
      fetch("/v1/me")
        .then((res) => res.json())
        .then((data: { role?: string }) => {
          if (data.role) {
            setUser({
              id: sessionData.user.id,
              name: sessionData.user.name,
              email: sessionData.user.email,
              image: sessionData.user.image ?? null,
              role: data.role as "admin" | "member",
            });
          }
        })
        .catch(() => {
          // Ignore — role defaults to "member"
        });
    }
  }, [sessionData?.user, setUser]);

  useEffect(() => {
    if (defaultsHydratedRef.current) {
      return;
    }

    if (isPreferencesPending || isModelsPending) {
      return;
    }

    const mode = preferencesData?.preferences.defaultMode ?? "build";
    const modelId =
      preferencesData?.preferences.defaultModelId ?? modelsData?.defaultModelId ?? null;

    setComposerMode(mode);
    setComposerModelId(modelId);
    defaultsHydratedRef.current = true;
  }, [
    isPreferencesPending,
    isModelsPending,
    preferencesData?.preferences.defaultMode,
    preferencesData?.preferences.defaultModelId,
    modelsData?.defaultModelId,
    setComposerMode,
    setComposerModelId,
  ]);

  // Sync URL → Store on mount
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const thread = params.get("thread");
    const automation = params.get("automation");
    const view = params.get("view");
    const doc = params.get("doc");
    if (thread) {
      selectSession(thread);
    } else if (automation) {
      selectAutomation(automation);
    } else if (view === "docs") {
      if (doc) {
        setDocSlug(doc);
      } else {
        setView("docs");
      }
    } else if (view === "admin") {
      setView("admin");
    } else if (view === "cli-auth") {
      setView("cli-auth");
    } else if (view === "stats") {
      setView("stats");
    } else if (view === "watches") {
      setView("watches");
    } else if (view === "preferences") {
      setView("preferences");
    } else {
      setView("sessions");
    }
  }, [selectSession, selectAutomation, setDocSlug, setView]);

  // Handle browser back/forward navigation
  useEffect(() => {
    const handlePopState = () => {
      const params = new URLSearchParams(window.location.search);
      const thread = params.get("thread");
      const automation = params.get("automation");
      const view = params.get("view");
      const doc = params.get("doc");
      if (thread) {
        selectSession(thread);
      } else if (automation) {
        selectAutomation(automation);
      } else if (view === "docs") {
        if (doc) {
          setDocSlug(doc);
        } else {
          setView("docs");
        }
      } else if (view === "admin") {
        setView("admin");
      } else if (view === "cli-auth") {
        setView("cli-auth");
      } else if (view === "stats") {
        setView("stats");
      } else if (view === "watches") {
        setView("watches");
      } else if (view === "preferences") {
        setView("preferences");
      } else {
        setView("sessions");
      }
    };

    window.addEventListener("popstate", handlePopState);
    return () => window.removeEventListener("popstate", handlePopState);
  }, [selectSession, selectAutomation, setDocSlug, setView]);

  return (
    <div className="flex h-screen">
      <Sidebar />
      <main className="flex min-w-0 flex-1 flex-col">
        <MobileHeader />
        <MainContent />
      </main>
    </div>
  );
}

function AuthenticatedApp() {
  const { data: sessionData, isPending } = useSession();

  // Show loading state while checking auth
  if (isPending) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="text-cyber-muted">Loading...</div>
      </div>
    );
  }

  // Show login page if not authenticated
  if (!sessionData?.user) {
    return <LoginPage />;
  }

  // Show dashboard if authenticated
  return <Dashboard />;
}

export default function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <div className="scanlines h-screen overflow-hidden bg-cyber-bg">
        <div className="relative z-10 h-full">
          <AuthenticatedApp />
        </div>
      </div>
      <Toast />
    </QueryClientProvider>
  );
}
